import '../main';
import { Extension } from '@tiptap/core';
import { parseStyleString, responsiveVarName, stringifyStyleObject } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
function getBreakpoint(editor) {
    var _a, _b;
    return ((_b = (_a = editor === null || editor === void 0 ? void 0 : editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || 'lg';
}
function getResponsiveConfig(options) {
    if (!options.responsive || !options.responsive.enabled)
        return null;
    return {
        prefix: options.responsive.prefix || 'vp',
        cssPropLineHeight: options.responsive.cssPropLineHeight || 'line-height',
    };
}
/**
 * This extension allows you to set the line-height for text.
 * @see https://www.tiptap.dev/api/extensions/line-height
 */
export const LineHeight = Extension.create({
    name: 'lineHeight',
    addOptions() {
        return {
            types: ['textStyle'],
            responsive: {
                enabled: true,
                prefix: 'vp',
                cssPropLineHeight: 'line-height',
            },
        };
    },
    addGlobalAttributes() {
        const cfg = getResponsiveConfig(this.options);
        const prefix = (cfg === null || cfg === void 0 ? void 0 : cfg.prefix) || 'vp';
        const varPrefix = `--${prefix}-`;
        return [
            {
                types: this.options.types,
                attributes: {
                    style: {
                        default: null,
                        parseHTML: element => {
                            const styleAttr = element.getAttribute('style') || '';
                            if (!styleAttr || !cfg)
                                return null;
                            const styleObj = parseStyleString(styleAttr);
                            const responsiveVars = {};
                            Object.entries(styleObj).forEach(([key, value]) => {
                                if (key.startsWith(varPrefix)) {
                                    responsiveVars[key] = value;
                                }
                            });
                            const nextStyle = stringifyStyleObject(responsiveVars);
                            return nextStyle || null;
                        },
                        renderHTML: attributes => {
                            if (!attributes.style || !cfg) {
                                return {};
                            }
                            return {
                                style: attributes.style,
                            };
                        },
                    },
                    lineHeight: {
                        default: null,
                        parseHTML: element => element.style.lineHeight,
                        renderHTML: attributes => {
                            if (!attributes.lineHeight) {
                                return {};
                            }
                            // In responsive mode we keep line-height as CSS vars on `style`.
                            // Avoid emitting a fixed inline line-height from this attribute.
                            if (cfg) {
                                return {};
                            }
                            return {
                                style: `line-height: ${attributes.lineHeight}`,
                            };
                        },
                    },
                },
            },
        ];
    },
    // @ts-ignore
    addCommands() {
        return {
            setLineHeight: (lineHeight, opts) => ({ editor, chain }) => {
                const cfg = getResponsiveConfig(this.options);
                // Non-responsive behavior (backwards compatible)
                if (!cfg) {
                    return chain().setMark('textStyle', { lineHeight }).run();
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const lhVar = responsiveVarName(cfg.cssPropLineHeight, bp, cfg.prefix);
                // Merge into existing textStyle.style
                const attrs = editor.getAttributes('textStyle') || {};
                const styleObj = parseStyleString(attrs.style || '');
                // Legacy compatibility: remove fixed line-height so responsive vars are authoritative.
                delete styleObj['line-height'];
                styleObj[lhVar] = lineHeight;
                // Store the responsive var only; CSS applies line-height per breakpoint.
                return chain()
                    .setMark('textStyle', {
                    ...attrs,
                    style: stringifyStyleObject(styleObj),
                    lineHeight: null,
                })
                    .run();
            },
            unsetLineHeight: (opts) => ({ editor, chain }) => {
                const cfg = getResponsiveConfig(this.options);
                // Non-responsive behavior
                if (!cfg) {
                    return chain().setMark('textStyle', { lineHeight: null }).removeEmptyTextStyle().run();
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const lhVar = responsiveVarName(cfg.cssPropLineHeight, bp, cfg.prefix);
                const attrs = editor.getAttributes('textStyle') || {};
                const styleObj = parseStyleString(attrs.style || '');
                delete styleObj['line-height'];
                delete styleObj[lhVar];
                return chain()
                    .setMark('textStyle', {
                    ...attrs,
                    style: stringifyStyleObject(styleObj),
                    lineHeight: null,
                })
                    .removeEmptyTextStyle()
                    .run();
            },
        };
    }
});
